import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { useWebSocket } from '../hooks/useWebSocket';
import { useFilters } from '../hooks/useFilters';
import { useLivePrices } from '../hooks/useLivePrices';
import { useCoinNews } from '../hooks/useCoinNews';
import { useInsights } from '../hooks/useInsights';
import { API_BASE_URL } from '../utils/apiConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { formatNumber, getExchangeLogo, DEFAULT_COIN_ICON } from '../utils/helpers';
import { rewriteR2Url } from '../utils/r2Url';
import HyperliquidPositionsWidget from '../components/HyperliquidPositionsWidget/HyperliquidPositionsWidget';
import TrendingCoinsWidget from '../components/TrendingCoinsWidget/TrendingCoinsWidget';
import './CoinPage.css';

// Historical prices are now fetched from API (see useEffect below)

// In-memory cache for CoinGecko market data (persists across navigation, clears on page reload)
// This avoids unnecessary API calls when user navigates between coin pages
const marketDataCache = new Map();

// Module-level cache for historical prices (persists across navigation)
// Historical prices are facts that don't change, so cache aggressively
const historicalPricesCache = new Map();

// Memoized Stats Grid component - only re-renders when summary changes
const StatsGrid = memo(function StatsGrid({ summary, longShortRatio, t }) {
  return (
    <div className="coin-page-stats">
      <div className="coin-page-stat-card">
        <div className="coin-page-stat-header">
          <span className="coin-page-stat-icon">&#128051;</span>
          <span>{t('coinPage.whaleTrades', 'Whale Trades')}</span>
        </div>
        <div className="coin-page-stat-value">{summary?.totalWhaleTrades || 0}</div>
        <div className="coin-page-stat-sub">${formatNumber(summary?.whaleVolume || 0)}</div>
      </div>

      <div className="coin-page-stat-card">
        <div className="coin-page-stat-header">
          <span className="coin-page-stat-icon">&#128293;</span>
          <span>{t('coinPage.liquidations', 'Liquidations')}</span>
        </div>
        <div className="coin-page-stat-value">{summary?.totalLiquidations || 0}</div>
        <div className="coin-page-stat-sub">${formatNumber(summary?.liqVolume || 0)}</div>
      </div>

      <div className="coin-page-stat-card">
        <div className="coin-page-stat-header">
          <span className="coin-page-stat-icon">&#128202;</span>
          <span>{t('coinPage.volume', 'Volume')}</span>
        </div>
        <div className="coin-page-stat-value">${formatNumber(summary?.totalVolume || 0)}</div>
        <div className="coin-page-stat-sub">24h volume</div>
      </div>

      <div className="coin-page-stat-card">
        <div className="coin-page-stat-header">
          <span className="coin-page-stat-icon">&#9878;</span>
          <span>{t('coinPage.longShort', 'Long / Short')}</span>
        </div>
        <div className="coin-page-ls-bar">
          <div className="coin-page-ls-long" style={{ width: `${longShortRatio.longPercent}%` }}></div>
          <div className="coin-page-ls-short" style={{ width: `${longShortRatio.shortPercent}%` }}></div>
        </div>
        <div className="coin-page-ls-labels">
          <span className="coin-page-ls-long-label">{longShortRatio.longPercent}%</span>
          <span className="coin-page-ls-short-label">{longShortRatio.shortPercent}%</span>
        </div>
      </div>

      <a
        href="https://app.hyperliquid.xyz/join/TRADERMAP"
        target="_blank"
        rel="noopener noreferrer"
        className="coin-page-stat-card coin-page-hl-card"
      >
        <div className="coin-page-stat-header">
          <span className="coin-page-stat-icon">&#127919;</span>
          <span>Hyperliquid</span>
        </div>
        <div className="coin-page-hl-card-value">Trade now</div>
        <div className="coin-page-hl-card-badge">4% OFF</div>
      </a>
    </div>
  );
});

// Memoized Trade Item component
const TradeItem = memo(function TradeItem({ trade, formatTime, type }) {
  return (
    <div className="coin-page-trade-item">
      <div className="coin-page-trade-left">
        <img
          src={trade.exchangeLogo || getExchangeLogo(trade.exchange) || '/assets_new/img/HL symbol_white.svg'}
          alt=""
          className="coin-page-trade-exchange"
        />
        <span className="coin-page-trade-time">{formatTime(trade.timestamp)}</span>
      </div>
      <div className="coin-page-trade-right">
        <span className="coin-page-trade-amount">${formatNumber(trade.dollarSize)}</span>
        <span className={`coin-page-trade-badge ${type === 'whale' ? (trade.side === 'buy' ? 'long' : 'short') : (trade.side === 'sell' ? 'long' : 'short')}`}>
          {type === 'whale'
            ? (trade.side === 'buy' ? 'LONG' : 'SHORT')
            : (trade.side === 'sell' ? 'LONG' : 'SHORT')
          }
        </span>
      </div>
    </div>
  );
});

// Memoized News Section - displays real coin news from RSS feeds
const NewsSection = memo(function NewsSection({ news, loading, t, coinName }) {
  // Show loading skeleton
  if (loading) {
    return (
      <div className="coin-page-news">
        <h2 className="coin-page-news-header">{t('coinPage.latestNews', 'Latest News')}</h2>
        <div className="coin-page-news-list">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="coin-page-news-item coin-page-news-skeleton">
              <div className="coin-page-news-title-skeleton"></div>
              <div className="coin-page-news-meta-skeleton"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show empty state if no news
  if (!news || news.length === 0) {
    return (
      <div className="coin-page-news">
        <h2 className="coin-page-news-header">{t('coinPage.latestNews', 'Latest News')}</h2>
        <div className="coin-page-news-empty">
          <span className="coin-page-news-empty-icon">&#128240;</span>
          <p>{t('coinPage.noNews', 'No recent news for {{coinName}}', { coinName })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coin-page-news">
      <h2 className="coin-page-news-header">{t('coinPage.latestNews', 'Latest News')}</h2>
      <div className="coin-page-news-list">
        {news.map((item) => (
          <div
            key={item.guid}
            className="coin-page-news-item"
          >
            <div className="coin-page-news-title">{item.title}</div>
            <div className="coin-page-news-meta">
              <span className="coin-page-news-source">{item.source}</span>
              <span className="coin-page-news-time">{item.time}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

function CoinPage() {
  const { coinSymbol } = useParams();
  const { t, i18n } = useTranslation();
  const { localizedPath, language } = useLanguage();
  const navigate = useNavigate();

  const coin = coinSymbol?.toUpperCase() || 'BTC';

  // Filters for WebSocket
  const { filters } = useFilters();

  // WebSocket connection for coin data
  const {
    isConnected,
    isSubscribed,
    subscriptionError,
    coinData,
    coinSummary,
    requestCoinData,
    subscribeToCoin,
    unsubscribeFromCoin,
    clearCoinData
  } = useWebSocket(filters, { enabled: true, playSounds: false });

  // Market data from bubbles API
  const [marketData, setMarketData] = useState(null);

  // Coin settings (description + TradingView code) from admin
  const [coinSettings, setCoinSettings] = useState(null);

  // Live prices from exchange WebSocket streams (primary), CoinGecko as fallback
  const { prices, formatPrice, getPrice } = useLivePrices();

  // Coin-specific news from RSS feeds
  const { news: coinNews, loading: newsLoading } = useCoinNews(coin);

  // Latest blog insight for this coin
  const { posts: latestInsights } = useInsights({ limit: 1, category: coin, lang: language });
  const latestInsight = latestInsights?.[0] || null;

  // Memoize currentPrice to avoid re-renders when other coins' prices change
  const currentPrice = useMemo(() => {
    return getPrice(coin) || marketData?.price || 0;
  }, [getPrice, coin, marketData?.price]);

  // Extract data from WebSocket (use directly, no transformation to avoid re-renders)
  const whaleTrades = coinData.whaleTrades || [];
  const liquidations = coinData.liquidations || [];
  const summary = coinSummary;
  const loading = coinData.loading;
  const loadingMore = coinData.loadingMore;
  const hasMore = coinData.hasMore;

  // Sort mode: 'time' (WebSocket, real-time) or 'size' (API, static)
  const [sortBy, setSortBy] = useState('time');
  const [sizeTradesData, setSizeTradesData] = useState({
    whale: [],
    liq: [],
    loading: false
  });

  // Fetch size-sorted trades from API when sortBy='size'
  useEffect(() => {
    if (sortBy !== 'size') return;

    const fetchSizeSorted = async () => {
      setSizeTradesData(prev => ({ ...prev, loading: true }));
      try {
        const [whaleRes, liqRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/coin/${coin}/trades?sortBy=size&type=whale&limit=100`),
          fetch(`${API_BASE_URL}/api/coin/${coin}/trades?sortBy=size&type=liquidation&limit=100`)
        ]);
        const whaleData = await whaleRes.json();
        const liqData = await liqRes.json();

        setSizeTradesData({
          whale: whaleData.success ? whaleData.trades : [],
          liq: liqData.success ? liqData.trades : [],
          loading: false
        });
      } catch (err) {
        console.error('Failed to fetch size-sorted trades:', err);
        setSizeTradesData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchSizeSorted();
  }, [coin, sortBy]);

  // Display trades based on sort mode (memoized to prevent re-renders)
  const displayWhaleTrades = useMemo(() =>
    sortBy === 'time' ? whaleTrades : sizeTradesData.whale,
    [sortBy, whaleTrades, sizeTradesData.whale]
  );
  const displayLiquidations = useMemo(() =>
    sortBy === 'time' ? liquidations : sizeTradesData.liq,
    [sortBy, liquidations, sizeTradesData.liq]
  );
  const isLoadingTrades = sortBy === 'time' ? loading : sizeTradesData.loading;

  // Infinite scroll handler (same as modal)
  const handleTradesScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled 80% and has more data
    if (scrollPercentage > 0.8 && hasMore && !loadingMore && !loading) {
      requestCoinData(coin, false);
    }
  }, [hasMore, loadingMore, loading, requestCoinData, coin]);

  // Calculate long/short ratio from summary
  const longShortRatio = useMemo(() => {
    if (!summary) return { longPercent: 50, shortPercent: 50, longVolume: 0, shortVolume: 0 };
    const longVol = summary.longVolume || 0;
    const shortVol = summary.shortVolume || 0;
    const total = longVol + shortVol;
    if (total === 0) return { longPercent: 50, shortPercent: 50, longVolume: 0, shortVolume: 0 };
    return {
      longPercent: Math.round((longVol / total) * 100),
      shortPercent: Math.round((shortVol / total) * 100),
      longVolume: longVol,
      shortVolume: shortVol
    };
  }, [summary]);

  // Investment calculator state
  const [calcPeriod, setCalcPeriod] = useState('1Y');
  const [historicalPrices, setHistoricalPrices] = useState({});
  const [historicalLoading, setHistoricalLoading] = useState(true);

  const [symbolsCache, setSymbolsCache] = useState({});

  // TradingView widget ref
  const tvContainerRef = useRef(null);

  // Load symbols cache
  useEffect(() => {
    const loadCache = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/icons/all`);
        if (res.ok) {
          setSymbolsCache(await res.json());
          return;
        }
      } catch (e) {}
      try {
        const res = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (res.ok) setSymbolsCache(await res.json());
      } catch (e) {}
    };
    loadCache();
  }, []);

  // Load historical prices for Investment Calculator (on-demand per coin)
  useEffect(() => {
    const loadHistoricalPrices = async () => {
      // Check client cache first (historical prices are facts that don't change)
      if (historicalPricesCache.has(coin)) {
        console.log(`[CoinPage] Using cached historical prices for ${coin}`);
        setHistoricalPrices(prev => ({
          ...prev,
          [coin]: historicalPricesCache.get(coin)
        }));
        setHistoricalLoading(false);
        return;
      }

      try {
        setHistoricalLoading(true);
        console.log(`[CoinPage] Fetching historical prices for ${coin}...`);
        const res = await fetch(`${API_BASE_URL}/api/historical-prices/${coin}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.prices) {
            // Save to client cache
            historicalPricesCache.set(coin, data.prices);
            // Merge with existing prices (keep other coins)
            setHistoricalPrices(prev => ({
              ...prev,
              [coin]: data.prices
            }));
            console.log(`[CoinPage] Historical prices loaded for ${coin}:`, Object.keys(data.prices));
          } else if (data.loading) {
            // Data is loading in background - retry after delay
            console.log(`[CoinPage] Historical prices loading for ${coin}, retrying in 5s...`);
            setTimeout(() => loadHistoricalPrices(), 5000);
          } else {
            console.warn(`[CoinPage] No historical prices available for ${coin}`);
          }
        } else {
          console.error(`[CoinPage] Failed to load historical prices for ${coin}: ${res.status}`);
        }
      } catch (e) {
        console.error(`[CoinPage] Failed to load historical prices for ${coin}:`, e);
      } finally {
        setHistoricalLoading(false);
      }
    };
    loadHistoricalPrices();
  }, [coin]);  // Reload when coin changes

  // Subscribe to coin data via WebSocket
  useEffect(() => {
    if (!isConnected || !isSubscribed) return;

    // Request initial data and subscribe
    requestCoinData(coin, true);
    subscribeToCoin(coin);

    // Cleanup: unsubscribe when leaving or changing coin
    return () => {
      unsubscribeFromCoin(coin);
      clearCoinData();
    };
  }, [coin, isConnected, isSubscribed, requestCoinData, subscribeToCoin, unsubscribeFromCoin, clearCoinData]);

  // Fetch coin settings first, then market data using coingeckoSymbol if available
  useEffect(() => {
    const fetchData = async () => {
      // 1. Fetch coin settings first (with current language for localized description)
      const currentLang = i18n.language?.split('-')[0] || 'en'; // Handle cases like 'en-US'
      let settings = null;
      try {
        const settingsRes = await fetch(`${API_BASE_URL}/api/coin-settings/${coin}?lang=${currentLang}`);
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          if (settingsData.success && settingsData.settings) {
            settings = settingsData.settings;
            setCoinSettings(settings);
          } else {
            setCoinSettings(null);
          }
        }
      } catch (e) {
        console.error('Failed to fetch coin settings');
        setCoinSettings(null);
      }

      // 2. Get CoinGecko ID (prioritize settings, then API lookup from Bubbles mapping)
      console.log(`[CoinPage] Settings for ${coin}:`, settings);

      let coingeckoId = settings?.coingeckoSymbol;

      if (!coingeckoId) {
        // Try to get from server mapping (Bubbles data has correct IDs like "zero-gravity" for 0G)
        try {
          const idRes = await fetch(`${API_BASE_URL}/api/coingecko-id/${coin}`);
          if (idRes.ok) {
            const idData = await idRes.json();
            if (idData.coinGeckoId) {
              coingeckoId = idData.coinGeckoId;
              console.log(`[CoinPage] Got CoinGecko ID from Bubbles mapping: ${coingeckoId}`);
            }
          }
        } catch (e) {
          console.warn('[CoinPage] Failed to get CoinGecko ID from API');
        }
      }

      // Fallback to symbol lowercase
      coingeckoId = coingeckoId || coin.toLowerCase();

      // Check cache first (market data doesn't change frequently)
      const cacheKey = coingeckoId;
      if (marketDataCache.has(cacheKey)) {
        console.log(`[CoinPage] Using cached market data for ${coingeckoId}`);
        setMarketData(marketDataCache.get(cacheKey));
        return;
      }

      console.log(`[CoinPage] Fetching from CoinGecko with ID: ${coingeckoId}`);

      try {
        const cgRes = await fetch(
          `https://api.coingecko.com/api/v3/coins/${coingeckoId}?localization=false&tickers=false&community_data=false&developer_data=false`
        );

        if (cgRes.ok) {
          const cgData = await cgRes.json();
          console.log(`[CoinPage] CoinGecko response for ${coingeckoId}:`, cgData.name, cgData.market_data?.current_price?.usd);
          const data = {
            price: cgData.market_data?.current_price?.usd || 0,
            priceChange24h: cgData.market_data?.price_change_percentage_24h || 0,
            marketCap: cgData.market_data?.market_cap?.usd || 0,
            volume24h: cgData.market_data?.total_volume?.usd || 0,
            name: cgData.name || coin,
            image: cgData.image?.large || cgData.image?.small || null
          };
          // Cache the result
          marketDataCache.set(cacheKey, data);
          setMarketData(data);
        } else {
          console.error(`[CoinPage] CoinGecko API error: ${cgRes.status}`);
          setMarketData(null);
        }
      } catch (e) {
        console.error('[CoinPage] Failed to fetch from CoinGecko:', e);
        setMarketData(null);
      }
    };
    fetchData();
  }, [coin, i18n.language]); // Language needed for coin settings, market data uses cache

  // Initialize TradingView widget
  useEffect(() => {
    if (!tvContainerRef.current) return;

    // Clear previous widget
    tvContainerRef.current.innerHTML = '';

    // Create widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.id = 'tradingview_widget_coin';
    tvContainerRef.current.appendChild(widgetContainer);

    // Use custom TradingView code if available, otherwise default to BINANCE perpetual (USDT.P)
    const tvSymbol = coinSettings?.tradingviewCode || `BINANCE:${coin}USDT.P`;

    // Load TradingView script
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = () => {
      if (window.TradingView) {
        new window.TradingView.widget({
          width: '100%',
          height: 500,
          symbol: tvSymbol,
          interval: 'D',
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1',
          locale: 'en',
          toolbar_bg: '#000000',
          enable_publishing: false,
          hide_side_toolbar: false,
          allow_symbol_change: false,
          container_id: 'tradingview_widget_coin',
          backgroundColor: '#0f0f0f',
          gridColor: '#1a1a1a'
        });
      }
    };
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [coin, coinSettings?.tradingviewCode]);

  // Investment calculator
  const investmentResult = useMemo(() => {
    const coinPrices = historicalPrices[coin];
    const historicalPrice = coinPrices?.[calcPeriod];

    // No data available for this coin/period
    if (!historicalPrice || !currentPrice) {
      return { value: 0, gain: 0, available: false };
    }

    const value = (1000 / historicalPrice) * currentPrice;
    const gain = ((value - 1000) / 1000) * 100;
    return { value, gain, available: true };
  }, [coin, currentPrice, calcPeriod, historicalPrices]);

  // Format time (memoized to prevent recreation on each render)
  const formatTime = useCallback((timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }, []);

  // Get icon URL - prioritize R2 over CoinGecko (memoized)
  const getIconUrl = useCallback((symbol, coingeckoFallback = null) => {
    const upperSymbol = symbol.toUpperCase();
    // Try different symbol variants in R2 cache
    const variants = [`${upperSymbol}USDT`, `${upperSymbol}USD`, `${upperSymbol}PERP`, upperSymbol];
    for (const variant of variants) {
      const url = symbolsCache[variant];
      if (url && (url.includes('r2.dev') || url.includes('cdn.tradermap.io'))) {
        return rewriteR2Url(url);
      }
    }
    // Fallback to CoinGecko image if provided
    if (coingeckoFallback) {
      return coingeckoFallback;
    }
    // Last resort: placeholder
    return DEFAULT_COIN_ICON;
  }, [symbolsCache]);

  // Price change color (memoized)
  const priceChangePositive = useMemo(() =>
    (marketData?.priceChange24h || 0) >= 0,
    [marketData?.priceChange24h]
  );

  // Memoized coin icon URL - only recalculated when coin or marketData changes
  const coinIconUrl = useMemo(() =>
    getIconUrl(coin, marketData?.image),
    [getIconUrl, coin, marketData?.image]
  );

  // Memoized formatted price string
  const formattedPrice = useMemo(() =>
    formatPrice ? formatPrice(currentPrice) : `$${currentPrice.toLocaleString()}`,
    [formatPrice, currentPrice]
  );

  // Memoized price change string
  const priceChangeFormatted = useMemo(() => {
    const change = marketData?.priceChange24h || 0;
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  }, [marketData?.priceChange24h]);

  // Memoized SEO coin data - only updates when actual values change
  const seoCoinData = useMemo(() => ({
    price: currentPrice,
    priceChange24h: marketData?.priceChange24h,
    marketCap: marketData?.marketCap,
    volume24h: marketData?.volume24h,
    image: coinIconUrl,
    description: coinSettings?.description
  }), [currentPrice, marketData?.priceChange24h, marketData?.marketCap, marketData?.volume24h, coinIconUrl, coinSettings?.description]);

  return (
    <div className="coin-page">
      <SEO
        page="coinDetails"
        dynamicParams={{ coinSymbol: coin, coinName: marketData?.name || coin }}
        coinData={seoCoinData}
      />
      <Header
        isConnected={isConnected}
        isSubscribed={isSubscribed}
        subscriptionError={subscriptionError}
      />

      <div className="coin-page-container">
        {/* Page Header */}
        <div className="coin-page-header">
          <div className="coin-page-header-left">
            <img
              src={coinIconUrl}
              alt={coin}
              className="coin-page-icon"
              onError={(e) => { e.target.src = coinIconUrl; }}
            />
            <div className="coin-page-info">
              <h1 className="coin-page-title">
                {marketData?.name || coin} {marketData?.name && marketData.name !== coin ? <span className="coin-page-symbol">({coin})</span> : null}
              </h1>
              <div className="coin-page-price-row">
                <span className={`coin-page-price ${priceChangePositive ? 'positive' : 'negative'}`}>
                  {formattedPrice}
                </span>
                <span className={`coin-page-change ${priceChangePositive ? 'positive' : 'negative'}`}>
                  {priceChangeFormatted}
                </span>
              </div>
              <div className="coin-page-meta">
                {marketData?.marketCap && (
                  <span className="coin-page-meta-item">
                    Market Cap: ${formatNumber(marketData.marketCap)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid — moved up next to the title for at-a-glance read */}
          <StatsGrid summary={summary} longShortRatio={longShortRatio} t={t} />

        </div>

        {/* Main Layout */}
        <div className="coin-page-layout">
          {/* Left Section */}
          <div className="coin-page-left">
            {/* TradingView Chart */}
            <div className="coin-page-chart">
              <div className="coin-page-chart-header">
                <h2 className="coin-page-chart-title">{t('coinPage.priceChart', 'Price Chart')}</h2>
              </div>
              <div className="coin-page-chart-widget" ref={tvContainerRef}></div>
            </div>

            {/* Coin Description */}
            {coinSettings?.description && (
              <div className="coin-page-description">
                <div className="coin-page-description-header">
                  <h2 className="coin-page-description-title">
                    {t('coinPage.about', 'About')} {marketData?.name || coin}
                  </h2>
                </div>
                <div className="coin-page-description-content">
                  {coinSettings.description}
                </div>
              </div>
            )}

            {/* Trades Section */}
            <div className="coin-page-trades">
              <div className="coin-page-trades-grid">
                {/* Whale Activity */}
                <div className="coin-page-trade-column">
                  <div className="coin-page-trade-header">
                    <h3>&#128051; {t('coinPage.whaleActivity', 'Whale Activity')}</h3>
                    <div className="coin-page-sort-buttons">
                      <button
                        className={`coin-page-sort-btn ${sortBy === 'time' ? 'active' : ''}`}
                        onClick={() => setSortBy('time')}
                        title={t('coinPage.sortByTime', 'Sort by time')}
                      >
                        &#128336;
                      </button>
                      <button
                        className={`coin-page-sort-btn ${sortBy === 'size' ? 'active' : ''}`}
                        onClick={() => setSortBy('size')}
                        title={t('coinPage.sortBySize', 'Sort by size')}
                      >
                        &#128176;
                      </button>
                    </div>
                  </div>
                  <div
                    className="coin-page-trade-list"
                    onScroll={sortBy === 'time' ? handleTradesScroll : undefined}
                  >
                    {isLoadingTrades ? (
                      <div className="coin-page-loading">{t('common.loading', 'Loading...')}</div>
                    ) : displayWhaleTrades.length === 0 ? (
                      <div className="coin-page-empty">{t('coinPage.noTrades', 'No trades found')}</div>
                    ) : (
                      <>
                        {displayWhaleTrades.map((trade, index) => (
                          <TradeItem
                            key={`whale-${index}-${trade.timestamp}`}
                            trade={trade}
                            formatTime={formatTime}
                            type="whale"
                          />
                        ))}
                        {sortBy === 'time' && loadingMore && (
                          <div className="coin-page-loading-more">{t('common.loadingMore', 'Loading more...')}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Liquidations */}
                <div className="coin-page-trade-column">
                  <div className="coin-page-trade-header">
                    <h3>&#128293; {t('coinPage.liquidations', 'Liquidations')}</h3>
                    <div className="coin-page-sort-buttons">
                      <button
                        className={`coin-page-sort-btn ${sortBy === 'time' ? 'active' : ''}`}
                        onClick={() => setSortBy('time')}
                        title={t('coinPage.sortByTime', 'Sort by time')}
                      >
                        &#128336;
                      </button>
                      <button
                        className={`coin-page-sort-btn ${sortBy === 'size' ? 'active' : ''}`}
                        onClick={() => setSortBy('size')}
                        title={t('coinPage.sortBySize', 'Sort by size')}
                      >
                        &#128176;
                      </button>
                    </div>
                  </div>
                  <div className="coin-page-trade-list">
                    {isLoadingTrades ? (
                      <div className="coin-page-loading">{t('common.loading', 'Loading...')}</div>
                    ) : displayLiquidations.length === 0 ? (
                      <div className="coin-page-empty">{t('coinPage.noLiquidations', 'No liquidations found')}</div>
                    ) : (
                      displayLiquidations.map((trade, index) => (
                        <TradeItem
                          key={`liq-${index}-${trade.timestamp}`}
                          trade={trade}
                          formatTime={formatTime}
                          type="liquidation"
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Hyperliquid Positions Widget - Top 3 positions for this coin */}
            <HyperliquidPositionsWidget symbol={coin} limit={3} />

            {/* Info Boxes */}
            <div className="coin-page-info-boxes">
              <div className="coin-page-info-box">
                <h3 className="coin-page-info-title">{t('coinPage.analysis', 'Analytics')}</h3>
                <p className="coin-page-info-content">
                  {t('coinPage.analysisText', { coinName: marketData?.name || coin, coinSymbol: coin })}
                </p>
              </div>

              <div className="coin-page-info-box">
                <h3 className="coin-page-info-title">{t('coinPage.whaleTracker', 'Whale Tracker')}</h3>
                <p className="coin-page-info-content">
                  {t('coinPage.whaleTrackerText', { coinName: marketData?.name || coin, coinSymbol: coin })}
                </p>
              </div>

              <div className="coin-page-info-box">
                <h3 className="coin-page-info-title">{t('coinPage.newsSentiment', 'News & Sentiment')}</h3>
                <p className="coin-page-info-content">
                  {t('coinPage.newsSentimentText', { coinName: marketData?.name || coin, coinSymbol: coin })}
                </p>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="coin-page-right">
            {/* Investment Calculator */}
            <div className="coin-page-calculator">
              <div className="coin-page-calc-title">
                <span>&#128200;</span>
                <span>{t('coinPage.investmentCalculator', 'Investment Calculator')}</span>
              </div>
              {historicalLoading ? (
                <div className="coin-page-calc-loading">
                  <div className="coin-page-calc-spinner"></div>
                  <span>{t('common.loading', 'Loading...')}</span>
                </div>
              ) : investmentResult.available ? (
                <>
                  <div className={`coin-page-calc-amount ${investmentResult.gain >= 0 ? 'positive' : 'negative'}`}>
                    ${investmentResult.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="coin-page-calc-gain">
                    If you invested <strong>$1,000</strong>:
                    <span className={investmentResult.gain >= 0 ? 'positive' : 'negative'}>
                      {investmentResult.gain >= 0 ? '+' : ''}{investmentResult.gain.toFixed(0)}%
                    </span>
                  </div>
                </>
              ) : (
                <div className="coin-page-calc-unavailable">
                  <span>{t('coinPage.comingSoon', 'Coming soon...')}</span>
                </div>
              )}
              <div className="coin-page-calc-options">
                {['1M', '3M', '6M', '1Y', '2Y'].map(period => (
                  <button
                    key={period}
                    className={`coin-page-calc-option ${calcPeriod === period ? 'active' : ''} ${!historicalPrices[coin]?.[period] ? 'disabled' : ''}`}
                    onClick={() => historicalPrices[coin]?.[period] && setCalcPeriod(period)}
                    disabled={!historicalPrices[coin]?.[period]}
                  >
                    {period}
                  </button>
                ))}
              </div>
              <button
                className="coin-page-calc-full-btn"
                onClick={() => navigate(`/calculator/${coin}`)}
              >
                {t('coinPage.openFullCalculator', 'Open Full Calculator')}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            </div>

            {/* Latest Insight for this coin */}
            {latestInsight && (
              <Link to={localizedPath(`/insights/${latestInsight.slug}`)} className="coin-page-insight-card">
                {latestInsight.coverImage && (
                  <div className="coin-page-insight-image">
                    <img src={rewriteR2Url(latestInsight.coverImage)} alt={latestInsight.title} loading="lazy" />
                  </div>
                )}
                <div className="coin-page-insight-content">
                  <span className="coin-page-insight-label">{t('coinPage.latestAnalysis', 'Latest Analysis')}</span>
                  <h4 className="coin-page-insight-title">{latestInsight.title}</h4>
                  {latestInsight.excerpt && (
                    <p className="coin-page-insight-excerpt">{latestInsight.excerpt}</p>
                  )}
                  <span className="coin-page-insight-date">
                    {latestInsight.publishedAt && new Date(latestInsight.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </Link>
            )}

            {/* News Section - Memoized component */}
            <NewsSection
              news={coinNews}
              loading={newsLoading}
              t={t}
              coinName={marketData?.name || coin}
            />

            {/* Trending Coins Widget - Top Gainers */}
            <TrendingCoinsWidget limit={8} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoinPage;
